import { useEffect, useRef } from "react";

/**
 * Light-weight live-sync helper.
 *
 *   useLiveSync(loaderFn, { interval: 8000 })
 *
 * - Calls loaderFn() on mount.
 * - Re-calls every `interval` ms while the page is visible.
 * - Re-calls immediately when the tab regains focus (so a franchisee
 *   submitting an order shows up in the admin Kanban within seconds).
 * - Pauses while the tab is hidden to save bandwidth.
 *
 * Defaults to 8 s — fast enough to feel live on iPad, cheap on backend.
 */
export default function useLiveSync(loaderFn, { interval = 8000 } = {}) {
  const cb = useRef(loaderFn);
  cb.current = loaderFn;

  useEffect(() => {
    let alive = true;
    let timer = null;

    const tick = () => {
      if (!alive) return;
      if (document.visibilityState !== "visible") return;
      try { cb.current?.(); } catch (e) { console.error("[useLiveSync] tick failed", e); }
    };

    const start = () => {
      stop();
      tick();
      timer = setInterval(tick, interval);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        tick();
        if (!timer) start();
      } else {
        stop();
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", tick);
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", tick);
    };
  }, [interval]);
}
